import { useRef, useState } from "react";

import { Directory } from "../Directory/Directory";
import { DIRECTORY_CONFIG, SECTION_DIRECTORIES } from "../constants";
import { LeftSidebarDirectory, LeftSidebarSection } from "../types";
import { getResizeInfo } from "./resizeCalculator/getResizeInfo";
import {
  calculateNewFlexGrow,
  calculateOpenDirectory,
  normalizeFlexGrow,
} from "./resizeCalculator/resizeCalculator";
import { DirectoryState, getFlexGrow } from "./resizeCalculator/types";

import styles from "./Directories.module.scss";

interface DirectoriesProps {
  activeSection: LeftSidebarSection;
}

interface ResizeSession {
  resizingDirectory: LeftSidebarDirectory;
  containerHeight: number;
  lastDeltaY: number;
  currentStates: Record<LeftSidebarDirectory, DirectoryState>; // Track current state during resize
}

export const Directories = (props: DirectoriesProps) => {
  const { activeSection } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const resizeSessionRef = useRef<ResizeSession | null>(null);

  const directories = SECTION_DIRECTORIES[activeSection];

  // Track directory states (open/closed + flex-grow)
  const [directoryStates, setDirectoryStates] = useState<
    Record<LeftSidebarDirectory, DirectoryState>
  >(() => {
    const initial: Record<string, DirectoryState> = {};
    directories.forEach(dir => {
      initial[dir] = { isOpen: true, flexGrow: 1 }; // All open by default
    });
    return initial;
  });

  const toggleDirectory = (directory: LeftSidebarDirectory) => {
    setDirectoryStates(prev => {
      const currentState = prev[directory];

      if (!containerRef.current) return prev;

      if (currentState.isOpen) {
        // Closing: set flexGrow to 0 and normalize remaining open directories
        const updated = {
          ...prev,
          [directory]: { isOpen: false, flexGrow: 0 },
        };
        return normalizeFlexGrow(updated);
      }
      // Opening: use standalone calculation function with validation

      const containerHeight = containerRef.current.clientHeight;

      return calculateOpenDirectory(prev, directory, containerHeight);
    });
  };

  const handleResizeStart = (directory: LeftSidebarDirectory) => {
    if (!containerRef.current) return;

    resizeSessionRef.current = {
      resizingDirectory: directory,
      containerHeight: containerRef.current.clientHeight,
      lastDeltaY: 0,
      currentStates: directoryStates, // Capture initial state
    };
  };

  const handleResizeMove = (deltaY: number) => {
    const session = resizeSessionRef.current;
    if (!session) return;

    // Only apply the INCREMENTAL change since last update
    const incrementalDeltaY = deltaY - session.lastDeltaY;
    session.lastDeltaY = deltaY;

    // Use pure calculation function (pipeline approach)
    // IMPORTANT: Use session.currentStates, NOT directoryStates (which is stale!)
    const newDirectoryStates = calculateNewFlexGrow({
      directories: session.currentStates,
      resizingDirectory: session.resizingDirectory,
      deltaPixels: incrementalDeltaY,
      containerHeight: session.containerHeight,
    });

    // Update session with new state for next iteration
    session.currentStates = newDirectoryStates;

    setDirectoryStates(newDirectoryStates);
  };

  const handleResizeEnd = () => {
    resizeSessionRef.current = null;
  };

  return (
    <div
      ref={containerRef}
      className={styles.directories}
      data-directories-container
    >
      {directories.map(directory => {
        const state = directoryStates[directory];

        const { canResize } = getResizeInfo(directory, directoryStates);

        const DirectoryComponent = DIRECTORY_CONFIG[directory].component;
        const flexGrow = getFlexGrow(state);

        return (
          <Directory
            key={directory}
            title={DIRECTORY_CONFIG[directory].title}
            icon={DIRECTORY_CONFIG[directory].icon}
            controls={DIRECTORY_CONFIG[directory].controls}
            body={<DirectoryComponent />}
            isCollapsible={directories.length > 1}
            isOpen={state.isOpen}
            isResizable={canResize}
            flexGrow={flexGrow}
            onToggle={() => toggleDirectory(directory)}
            onResizeStart={() => handleResizeStart(directory)}
            onResizeMove={handleResizeMove}
            onResizeEnd={handleResizeEnd}
          />
        );
      })}
    </div>
  );
};
